import { ErrorHandler, Injectable, NgZone } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { NotificationRef, NotificationService } from "@progress/kendo-angular-notification";
import { NGXLogger } from "ngx-logger";
import { FileAlreadyExistsNotification } from "./document/file-already-exists.notification";
import { MittelErfolgreichAbgerufenNotification } from "./document/mittel-erfolgreich-abgerufen.notification";
import { FileTooLargeNotification } from "./document/file-too-large.notification";
import { InvalidMimeTypeNotification } from "./document/invalid-mime-type.notification";
import { DetailNotificationComponent } from "./detail-notification/detail-notification.component";
import { Router } from '@angular/router';
import { LoadingAnimationService } from '../../services/loading-animation.service';
import { I18nService } from "@/core/services/i18nService";
import { ExtendedAuthenticationService } from "@/core/components/authentication/services/extended-authentication.service";
import { environment } from "src/environments/environment";
import { FreigebenSendEmailFailedNotification } from "./freigeben/freigeben-send-email-failed.notification";

@Injectable({
    providedIn: 'root'
})
export class GlobalNotificationHandler implements ErrorHandler {

    private openNotificationArray: NotificationRef[] = [];

    constructor(protected notificationService: NotificationService, protected logger: NGXLogger, 
        protected router: Router, protected ngZone: NgZone, protected loadingAnimationService:LoadingAnimationService, 
        protected languageService: I18nService) { }

    handleError(error: Error | HttpErrorResponse): void {
        let errorObject = error as any;

        if(errorObject.rejection != undefined){
            error = errorObject.rejection
        }

         //needed for some errors that dont get deserialized properly
         if(error instanceof HttpErrorResponse && error.error != null && typeof error.error ==  'string'){
            console.log(typeof error.error)
            try{
                let innerError = JSON.parse(errorObject.error);
                console.log(innerError);
                error = new HttpErrorResponse({error: innerError, headers: error.headers, status: error.status, statusText: error.statusText} )
            }
            finally{}
        }
        
        console.log(error);
        if (error instanceof HttpErrorResponse && 
            (error.error == undefined || error.error == null || error.error?.Errors == undefined || error.error?.Errors?.length == 0)) {
            switch (error.status) {
                case 0:
                    this.showNotification(this.languageService.instant("errorhandler.unexpectedError"));
                    break;
                case 400:
                    this.showNotification(this.languageService.instant("errorhandler.validationError"));
                    break;
                case 401:
                    this.showNotification(this.languageService.instant("errorhandler.unauthorizedAccessError"));
                    break;
                case 500:
                    this.showNotification(this.languageService.instant("errorhandler.serverError"));
                    break;
            }
        }
        else if (error instanceof HttpErrorResponse && error.error.Errors != undefined && error.error.Errors != null) {
            error.error.Errors.forEach((ex: any, index: number) => {
                if(typeof ex == "string")
                    this.showNotification(ex, 'error');
                else{
                    if(environment.production == false){
                        this.showNotification( ex.Message, "error", ex.InnerExceptionMessage )
                    }
                    else{
                        this.showNotification( ex.Message, "error")
                    }
                }
            }); 
        }
        else if (error instanceof InvalidMimeTypeNotification) {
            this.showNotification(this.languageService.instant("errorhandler.invalidMimeTypeError"));
        }
        else if (error instanceof FileTooLargeNotification) {
            let innerError: FileTooLargeNotification = error;
            this.showNotification(innerError.DisplayMessage)
        }
        else if (error instanceof FileAlreadyExistsNotification) {
            let innerError: FileAlreadyExistsNotification = error;
            this.showNotification(innerError.DisplayMessage);
        }
        else if (error instanceof MittelErfolgreichAbgerufenNotification) {
            let innerError: MittelErfolgreichAbgerufenNotification = error;
            this.showNotification(innerError.DisplayMessage, 'success');
        }
        else if (error instanceof FreigebenSendEmailFailedNotification) {
            this.showNotification(this.languageService.instant("errorhandler.freigebenSendEmailFailedError"), 'error', undefined, true);
        }
        else {
            this.logger.error(error);
        }

        if(error instanceof HttpErrorResponse)
        switch(error.status){
            case 400: //Bad request -> e.g. GUID in URL is invalid
            case 401: //Unauthorized -> user has no access to anfrage which was navigated too
            case 403: //Forbidden 
            case 404: //NotFound 
                this.rerouteToRoot();
                return
                break;
        }
    }

    private rerouteToRoot(){
        this.ngZone.run(() => {
            this.loadingAnimationService.hide();
            this.loadingAnimationService.onTeardown();
            this.router.navigateByUrl("/")
        });
    }

    public showNotification(content: string,  type: 'error' | 'success' = 'error', detailedContent?: string, closeable: boolean = false) {
        let notificationRef : NotificationRef;
        if(detailedContent) {
             notificationRef = this.notificationService.show({
                closable: true,
                content: DetailNotificationComponent,
                animation: { type: "fade", duration: 500 },
                position: { horizontal: "center", vertical: "bottom" },
                type: { style: type, icon: true },
            });
    
            notificationRef.content!.instance.content = content;
            notificationRef.content!.instance.detailedContent = detailedContent;
        }
        else {
            notificationRef = this.notificationService.show({
                    hideAfter: 5000,
                    content: content,
                    animation: { type: "fade", duration: 500 },
                    position: { horizontal: "center", vertical: "bottom" },
                    type: { style: type, icon: true },
                    closable: closeable
            });
        }

        notificationRef.afterHide?.subscribe(() => {
            this.openNotificationArray.splice(this.openNotificationArray.indexOf(notificationRef, 1));
        });

        this.openNotificationArray.push(notificationRef);
    }

    public RemoveAllNotifications(){
        this.openNotificationArray.forEach(function(item, index,object){
            item.hide()
        });
    }
}